import React, { useState, useRef, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LabelList, Legend } from "recharts";
import { BiExpand, BiCollapse, BiFilterAlt } from "react-icons/bi";
import DataTable from "../../utils/components/DataTable";
import Loading from "../../utils/components/Loading";
import timeStampToDateFormat from "../../utils/helpers/timestampToDateFormat";
import { auth } from "../../App/routes";

const BAR_COLORS = ["#dc3545", "#198754", "#fd7e14", "#6c757d", "#0dcaf0", "#6f42c1", "#e83e8c", "#20c997"];

const STACKED_COLORS = [
  "#dc3545", // red
  "#ffc107", // yellow
  "#198754", // green
  "#fd7e14", // orange
  "#0d6efd", // blue
  "#6f42c1", // purple
  "#20c997", // teal
  "#6c757d", // gray
];

const STATUS_COLORS = [
  "#0d6efd", // blue
  "#dc3545", // red
  "#ffc107", // yellow
  "#198754", // green
  "#6f42c1", // purple
  "#fd7e14", // orange
  "#20c997", // teal
  "#6c757d", // gray
];

const MAX_TICK_LEN = 14;


const TruncatedTick = ({ x, y, payload }) => {
  const full = payload?.value || '';
  const label = full.length > MAX_TICK_LEN ? full.slice(0, MAX_TICK_LEN) + '…' : full;
  return (
    <g transform={`translate(${x},${y})`}>
      {full.length > MAX_TICK_LEN && <title>{full}</title>}
      <text x={0} y={0} dy={4} textAnchor="end" fill="#666" fontSize={10} transform="rotate(-30)">
        {label}
      </text>
    </g>
  );
};

const DisciplineChart = ({ disciplineChart, onBarClick, selectedBarName, selectedStackName }) => {
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      setChartWidth(containerRef.current.offsetWidth);
    }
  }, []);

  if (!disciplineChart?.data?.length) return null;
  const { statuses, data, y_label = "Gözlem Sayısı", x_label = "Disiplin" } = disciplineChart;
  const effectiveWidth = Math.min(chartWidth, data.length * 160 + 60);

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {chartWidth > 0 && (
        <div style={{ display: "flex", justifyContent: "center" }}>
        <BarChart
          width={effectiveWidth}
          height={300}
          data={data}
          margin={{ top: 5, right: 10, left: 20, bottom: 75 }}
        >
          <XAxis
            dataKey="name"
            tick={<TruncatedTick />}
            interval={0}
            label={{ value: x_label, position: "insideBottom", offset: -60, style: { fontSize: 11, textAnchor: "middle" } }}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            label={{ value: y_label, angle: -90, position: "insideLeft", offset: -5, style: { fontSize: 11, textAnchor: "middle" } }}
          />
          <Tooltip />
          <Legend
            verticalAlign="top"
            align="center"
            iconType="circle"
            iconSize={10}
            wrapperStyle={{ fontSize: 12, paddingBottom: 8, top: -18 }}
          />
          {statuses.map((s, i) => (
            <Bar
              key={s}
              dataKey={s}
              stackId="a"
              fill={STACKED_COLORS[i % STACKED_COLORS.length]}
              barSize={40}
              radius={i === statuses.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
              onClick={onBarClick ? (barData) => onBarClick(barData.name, s) : undefined}
              style={onBarClick ? { cursor: "pointer" } : undefined}
            >
              {data.map((entry, j) => {
                const barSelected = !selectedBarName || entry.name === selectedBarName;
                const stackSelected = !selectedStackName || s === selectedStackName;
                const opacity = !selectedBarName ? 1 : (barSelected && stackSelected) ? 1 : 0.3;
                return (
                  <Cell
                    key={j}
                    fill={STACKED_COLORS[i % STACKED_COLORS.length]}
                    fillOpacity={opacity}
                  />
                );
              })}
              <LabelList
                dataKey={s}
                position="center"
                style={{ fill: "#fff", fontSize: 10, fontWeight: 600 }}
                formatter={(v) => (v > 0 ? v : "")}
              />
            </Bar>
          ))}
        </BarChart>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div
    className="rounded text-white text-center py-3 px-4 d-flex flex-column justify-content-center"
    style={{ backgroundColor: color, minWidth: 130, flexShrink: 0 }}
  >
    <div className="fw-bold" style={{ fontSize: 40, lineHeight: 1.1 }}>{value}</div>
    <div style={{ fontSize: 13, marginTop: 4 }}>{label}</div>
  </div>
);

const CreatorTable = ({ creatorStats, translate, selectedCreator, onCreatorFilter }) => {
  const t = (key) => translate(`project_settings.reports.default_report.${key}`);
  if (!creatorStats?.creators?.length) return null;
  const { statuses, creators } = creatorStats;

  return (
    <div className="table-responsive" style={{ maxHeight: 260, overflowY: "auto" }}>
      <table className="table table-sm align-middle" style={{ fontSize: 12 }}>
        <thead className="table-light sticky-top">
          <tr>
            <th>{translate("project_settings.reports.default_report.creator") || "Oluşturan"}</th>
            {statuses.map((s) => <th key={s} className="text-end">{s}</th>)}
            <th className="text-end fw-bold">{t("total")}</th>
          </tr>
        </thead>
        <tbody>
          {creators.map((c, i) => {
            const isSelected = selectedCreator === c.name;
            return (
              <tr
                key={i}
                onClick={() => onCreatorFilter(c.name)}
                style={{
                  cursor: "pointer",
                  backgroundColor: isSelected ? "#dbeafe" : undefined,
                  borderLeft: isSelected ? "4px solid #0d6efd" : "4px solid transparent",
                  transition: "background-color 0.15s",
                }}
              >
                <td>
                  <span>{c.name}</span>
                  {isSelected && (
                    <span
                      className="ms-2 badge rounded-pill text-bg-primary"
                      style={{ fontSize: 10, verticalAlign: "middle" }}
                    >
                      ×
                    </span>
                  )}
                </td>
                {statuses.map((s) => (
                  <td key={s} className="text-end">{c.counts[s] || ""}</td>
                ))}
                <td className="text-end fw-bold">{c.total}</td>
              </tr>
            );
          })}
          <tr className="table-light fw-bold">
            <td>{t("total")}</td>
            {statuses.map((s) => (
              <td key={s} className="text-end">
                {creators.reduce((sum, c) => sum + (c.counts[s] || 0), 0)}
              </td>
            ))}
            <td className="text-end">
              {creators.reduce((sum, c) => sum + c.total, 0)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const MetadataChart = ({ chart }) => {
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      setChartWidth(containerRef.current.offsetWidth);
    }
  }, []);

  return (
    <div ref={containerRef} className="flex-grow-1" style={{ minWidth: 220 }}>
      <div className="text-muted small fw-semibold mb-1 text-center">{chart.set_name}</div>
      {chartWidth > 0 && (
        <BarChart width={chartWidth} height={200} data={chart.data.slice(0, 15)} margin={{ top: 5, right: 5, left: -25, bottom: 40 }}>
          <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip />
          <Bar dataKey="count" radius={[2, 2, 0, 0]}>
            {chart.data.slice(0, 15).map((_, i) => (
              <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      )}
    </div>
  );
};

const DefaultReportTemplateSection = ({ template, translate, page, onPageChange, selectedCreator, onCreatorFilter, selectedChartFilter, onChartFilter, onClearFilters }) => {
  const t = (key) => translate(`project_settings.reports.default_report.${key}`);
  const tu = (key) => t(key).toLocaleUpperCase("en");
  const [expanded, setExpanded] = useState(false);
  const [tableHovered, setTableHovered] = useState(false);
  const hasActiveFilters = !!(selectedCreator || selectedChartFilter);
  const { stats, forms, creator_stats, metadata_charts, discipline_chart, field_chart } = template;

  const handleDisciplineBarClick = (barName, statusName) => {
    const barData = discipline_chart?.data?.find((d) => d.name === barName);
    if (!barData?.sub_id_number) return;
    const isSame = selectedChartFilter?.type === "discipline"
      && selectedChartFilter?.sub_id_number === barData.sub_id_number
      && selectedChartFilter?.status_name === statusName;
    onChartFilter?.(isSame ? null : { type: "discipline", sub_id_number: barData.sub_id_number, status_name: statusName });
  };

  const handleFieldBarClick = (barName) => {
    if (!field_chart?.field_code) return;
    const isSame = selectedChartFilter?.type === "field" && selectedChartFilter?.field_value === barName;
    onChartFilter?.(isSame ? null : { type: "field", field_code: field_chart.field_code, field_value: barName });
  };

  const disciplineSelectedBar = selectedChartFilter?.type === "discipline"
    ? discipline_chart?.data?.find((d) => d.sub_id_number === selectedChartFilter.sub_id_number)?.name ?? null
    : null;

  const disciplineSelectedStatus = selectedChartFilter?.type === "discipline"
    ? selectedChartFilter.status_name ?? null
    : null;

  const fieldSelectedBar = selectedChartFilter?.type === "field" ? selectedChartFilter.field_value : null;
  const totalRows = forms?.pagination?.total || 0;
  const isDetailLoaded = !!creator_stats;

  const extraCols = (template.extra_columns || []).map((col) => ({
    name: (col.field_label || col.field_code).toLocaleUpperCase("en"),
    selector: (row) => row.extra_fields?.[col.field_code] || "",
    sortable: true,
    wrap: true,
    format: col.field_type === "date/time"
      ? (row) => {
          const v = row.extra_fields?.[col.field_code];
          return v ? timeStampToDateFormat(Number(v), "dd/MM/yyyy") || v : "—";
        }
      : undefined,
  }));

  const columns = [
    {
      name: tu("form_no"),
      selector: (row) => row.no || "",
      sortable: true,
      cell: (row) => (
        <a
          href={auth.formDetails.link.replace(":id", row.id)}
          target="_blank"
          rel="noreferrer"
          className="fw-medium"
          style={{ textDecoration: "none" }}
          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
        >
          {row.no}
        </a>
      ),
    },
    {
      name: tu("location"),
      selector: (row) => row.location_path || "",
      sortable: true,
    },
    {
      name: tu("status"),
      selector: (row) => row.status_name || "",
      sortable: true,
    },
    {
      name: tu("open_date"),
      selector: (row) => row.created_at || 0,
      sortable: true,
      format: (row) => timeStampToDateFormat(row.created_at, "dd/MM/yyyy") || "—",
    },
    ...extraCols,
  ];

  return (
    <>
      <div>
        {/* Stat cards */}
        <div className="d-flex gap-3 mb-4" style={{ overflowX: "auto", paddingBottom: 2 }}>
          <StatCard label={t("total")} value={stats.total} color="#0d6efd" />
          {stats.by_status.map((s, i) => (
            <StatCard key={s.status_id} label={s.status_name} value={s.count} color={STATUS_COLORS[(i + 1) % STATUS_COLORS.length]} />
          ))}
        </div>

        {/* Creator table | Discipline chart | Metadata charts */}
        {!isDetailLoaded && (
          <div className="d-flex justify-content-center py-3">
            <Loading />
          </div>
        )}

        {isDetailLoaded && (
          <div className="row g-4 mb-4">
            {creator_stats?.creators?.length > 0 && (
              <div className={`col-12 ${discipline_chart ? "col-xl-4" : "col-xl-5"}`}>
                <CreatorTable
                  creatorStats={creator_stats}
                  translate={translate}
                  selectedCreator={selectedCreator}
                  onCreatorFilter={onCreatorFilter}
                />
              </div>
            )}
            {discipline_chart && (
              <div className="col-12 col-xl-4">
                <DisciplineChart
                  disciplineChart={discipline_chart}
                  onBarClick={handleDisciplineBarClick}
                  selectedBarName={disciplineSelectedBar}
                  selectedStackName={disciplineSelectedStatus}
                />
              </div>
            )}
            {(field_chart || metadata_charts?.length > 0) && (
              <div className={`col-12 ${discipline_chart ? "col-xl-4" : "col-xl-7"}`}>
                {field_chart && (
                  <DisciplineChart
                    disciplineChart={field_chart}
                    onBarClick={handleFieldBarClick}
                    selectedBarName={fieldSelectedBar}
                  />
                )}
                {!field_chart && metadata_charts?.length > 0 && (
                  <div className="d-flex flex-wrap gap-3">
                    {metadata_charts.map((chart, i) => (
                      <MetadataChart key={i} chart={chart} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Form table */}
        {forms?.data?.length > 0 && (
          <div
            style={{ position: "relative" }}
            onMouseEnter={() => setTableHovered(true)}
            onMouseLeave={() => setTableHovered(false)}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                zIndex: 5,
                display: "flex",
                gap: 4,
                opacity: tableHovered ? 1 : 0,
                transition: "opacity 0.15s",
                pointerEvents: tableHovered ? "auto" : "none",
              }}
            >
              {hasActiveFilters && (
                <button
                  style={{ background: "none", border: "none", padding: "3px 5px", cursor: "pointer", color: "#dc3545", borderRadius: 3, display: "flex", alignItems: "center", lineHeight: 1 }}
                  title="Filtreleri temizle"
                  onClick={onClearFilters}
                >
                  <BiFilterAlt style={{ fontSize: 18 }} />
                </button>
              )}
              <button
                style={{ background: "none", border: "none", padding: "3px 5px", cursor: "pointer", color: "#6c757d", borderRadius: 3, display: "flex", alignItems: "center", lineHeight: 1 }}
                onClick={() => setExpanded(true)}
                title="Tam ekran"
              >
                <BiExpand style={{ fontSize: 18 }} />
              </button>
            </div>
            <DataTable
              columns={columns}
              data={forms.data}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={20}
              paginationRowsPerPageOptions={[20]}
              onChangePage={(p) => onPageChange(p)}
              highlightOnHover
              striped
            />
          </div>
        )}
      </div>

      {/* Fullscreen overlay */}
      {expanded && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1050, background: "#fff", overflow: "auto", padding: 24 }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0">{template.name}</h5>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setExpanded(false)}>
              <BiCollapse />
            </button>
          </div>
          {forms?.data?.length > 0 && (
            <DataTable
              columns={columns}
              data={forms.data}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={20}
              paginationRowsPerPageOptions={[20]}
              onChangePage={(p) => onPageChange(p)}
              highlightOnHover
              striped
            />
          )}
        </div>
      )}
    </>
  );
};

export default DefaultReportTemplateSection;
